import type { Country } from "features/general/types";

export const parseBulkTokens = (text: string): string[] => {
    if (!text) {
        return [];
    }

    const tokens = text
        .split(/[,;\n\r\t]+|\s{2,}/g)
        .map(token => token.trim())
        .filter(Boolean);

    const seen = new Set<string>();
    const out: string[] = [];

    for (const token of tokens) {
        const key = token.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            out.push(token);
        }
    }

    return out;
};

export const isBulkPaste = (text: string): boolean => {
    if (!text) {
        return false;
    }

    if (/[,;\n\r\t]|\s{2,}/.test(text)) {
        return true;
    }

    return parseBulkTokens(text).length > 1;
};

type ResolveResult<T> = {
    readonly matched: T[];
    readonly unmatched: string[];
};

export const resolveCountryTokens = (
    tokens: string[],
    countries: readonly Country[]
): ResolveResult<string> => {
    const matched: string[] = [];
    const unmatched: string[] = [];
    const seen = new Set<string>();

    for (const rawToken of tokens) {
        const token = rawToken.trim().toLowerCase();
        if (!token) {
            continue;
        }

        const country = countries.find(c =>
            c.iso2.toLowerCase() === token ||
            c.iso3.toLowerCase() === token ||
            c.shortName.toLowerCase() === token ||
            c.longName.toLowerCase() === token
        );

        if (country && !seen.has(country.iso2)) {
            seen.add(country.iso2);
            matched.push(country.iso2);
        } else if (!country) {
            unmatched.push(rawToken);
        }
    }

    return { matched, unmatched };
};

export const resolveCurrencyTokens = (
    tokens: string[],
    options: readonly string[]
): ResolveResult<string> => {
    const matched: string[] = [];
    const unmatched: string[] = [];
    const seen = new Set<string>();

    for (const rawToken of tokens) {
        const token = rawToken.trim().toLowerCase();
        if (!token) {
            continue;
        }

        const option = options.find(o => o.toLowerCase() === token);

        if (option && !seen.has(option)) {
            seen.add(option);
            matched.push(option);
        } else if (!option) {
            unmatched.push(rawToken);
        }
    }

    return { matched, unmatched };
};

export const mergeUnique = (current: readonly string[], incoming: readonly string[]): string[] => {
    const seen = new Set(current.map(v => v.toLowerCase()));
    const out = [...current];

    for (const value of incoming) {
        const key = value.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            out.push(value);
        }
    }

    return out;
};
